import { useEffect, useState } from "react";
import { PencilSimple, XCircle, Plus, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtDate } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { TimeSelect, estimateHours } from "./TimeSelect";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  denied: "bg-red-100 text-red-700",
  cancelled: "bg-slate-100 text-slate-600",
};
const TYPE_LABEL = { pto: "PTO", sick: "Sick", unpaid: "Unpaid" };
const fmtTime = (t) => {
  if (!t) return null;
  const [h, m] = t.split(":");
  const hr = parseInt(h, 10);
  return `${((hr + 11) % 12) + 1}:${m} ${hr >= 12 ? "PM" : "AM"}`;
};
const timeSpan = (r) => (r.start_time || r.end_time)
  ? ` · ${r.start_time ? fmtTime(r.start_time) : "start of day"} → ${r.end_time ? fmtTime(r.end_time) : "end of day"}`
  : "";

const BLANK = { user_id: "", type: "pto", start_date: "", end_date: "", start_time: "", end_time: "", reason: "" };

export const AdminTimeOffManager = ({ onChanged, refreshKey = 0 }) => {
  const [rows, setRows] = useState([]);
  const [members, setMembers] = useState([]);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(null);
  const [cancelRow, setCancelRow] = useState(null);
  const [cancelComment, setCancelComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/timeoff/all", { params: { status } }).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [status, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/users").then((r) => setMembers(r.data.filter((u) => u.is_active !== false))).catch(() => {}); }, []);

  const openEdit = (r) => setDialog({
    mode: "edit", id: r.id, name: r.user_name,
    form: { user_id: r.user_id, type: r.type, start_date: r.start_date, end_date: r.end_date, start_time: r.start_time || "09:00", end_time: r.end_time || "17:00", reason: r.reason || "" },
  });

  const save = async () => {
    const f = dialog.form;
    const body = { ...f, hours_per_day: 8 };
    setBusy(true);
    try {
      if (dialog.mode === "add") {
        await api.post("/timeoff/admin", body);
        toast.success("Time off logged and approved — payroll updates automatically");
      } else {
        const { user_id, ...rest } = body;
        await api.put(`/timeoff/${dialog.id}/admin`, rest);
        toast.success("Request corrected — payroll updates automatically");
      }
      setDialog(null);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      await api.put(`/timeoff/${cancelRow.id}/cancel`, { comment: cancelComment });
      toast.success("Request cancelled — payroll updates automatically");
      setCancelRow(null); setCancelComment("");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cancel failed");
    } finally { setBusy(false); }
  };

  const setF = (patch) => setDialog((d) => ({ ...d, form: { ...d.form, ...patch } }));
  const filtered = rows.filter((r) => !search || r.user_name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="bg-card border border-border rounded-md" data-testid="admin-timeoff-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-border">
        <div>
          <h3 className="font-heading font-semibold tracking-tight">Manage team time off</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Log, correct or cancel any request — paychecks, analytics and the team calendar update automatically.</p>
        </div>
        <Button data-testid="log-timeoff-btn" size="sm" onClick={() => setDialog({ mode: "add", form: { ...BLANK } })} className="bg-[#0A0F1C] hover:bg-indigo-600 text-white transition-colors">
          <Plus size={15} className="mr-1" /> Log time off
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-2 px-6 py-3 border-b border-border">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="admin-search-input" placeholder="Search member…" className="h-8 w-48 pl-8" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="admin-status-filter" className="h-8 w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["all", "pending", "approved", "denied", "cancelled"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="divide-y divide-border">
        {filtered.map((r) => (
          <div key={r.id} data-testid={`admin-req-row-${r.id}`} className="px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium">{r.user_name} · {TYPE_LABEL[r.type]}</p>
              <p className="text-xs text-muted-foreground">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}{timeSpan(r)} · {r.total_hours}h</p>
              {r.reason && <p className="text-xs text-muted-foreground mt-0.5">Reason: "{r.reason}"</p>}
              {r.reviewer_comment && r.status !== "pending" && <p className="text-xs text-muted-foreground mt-0.5">"{r.reviewer_comment}"</p>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge className={STATUS_STYLE[r.status]}>{r.status}</Badge>
              {(r.status === "pending" || r.status === "approved") && (
                <>
                  <Button data-testid={`edit-req-btn-${r.id}`} size="sm" variant="outline" onClick={() => openEdit(r)} title="Correct this request">
                    <PencilSimple size={14} />
                  </Button>
                  <Button data-testid={`cancel-req-btn-${r.id}`} size="sm" variant="outline" onClick={() => { setCancelRow(r); setCancelComment(""); }}
                    className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700" title="Cancel this request">
                    <XCircle size={14} />
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="px-6 py-8 text-sm text-muted-foreground">No requests match.</p>}
      </div>

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-h-[88vh] overflow-y-auto" data-testid="admin-timeoff-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading">{dialog?.mode === "add" ? "Log time off for a member" : `Correct ${dialog?.name}'s request`}</DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="space-y-4">
              {dialog.mode === "add" && (
                <div>
                  <Label>Team member</Label>
                  <Select value={dialog.form.user_id} onValueChange={(v) => setF({ user_id: v })}>
                    <SelectTrigger data-testid="admin-member-select" className="mt-1.5"><SelectValue placeholder="Pick a member…" /></SelectTrigger>
                    <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Type</Label>
                <Select value={dialog.form.type} onValueChange={(v) => setF({ type: v })}>
                  <SelectTrigger data-testid="admin-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pto">PTO</SelectItem>
                    <SelectItem value="sick">Sick</SelectItem>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Start date</Label><Input data-testid="admin-start-date" type="date" className="mt-1.5" value={dialog.form.start_date} onChange={(e) => setF({ start_date: e.target.value })} /></div>
                <div><Label>End date</Label><Input data-testid="admin-end-date" type="date" className="mt-1.5" value={dialog.form.end_date} onChange={(e) => setF({ end_date: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Start time</Label><TimeSelect testId="admin-start-time" value={dialog.form.start_time} onChange={(v) => setF({ start_time: v })} /></div>
                <div><Label>End time</Label><TimeSelect testId="admin-end-time" value={dialog.form.end_time} onChange={(v) => setF({ end_time: v })} /></div>
              </div>
              {estimateHours(dialog.form.start_date, dialog.form.end_date, dialog.form.start_time, dialog.form.end_time) !== null && (
                <p className="text-sm font-medium rounded-md bg-secondary px-3 py-2" data-testid="admin-estimate">
                  ≈ {estimateHours(dialog.form.start_date, dialog.form.end_date, dialog.form.start_time, dialog.form.end_time)} hours (weekends excluded, calculated automatically)
                </p>
              )}
              <div>
                <Label>Reason (optional)</Label>
                <Textarea data-testid="admin-reason" className="mt-1.5" rows={2} value={dialog.form.reason} onChange={(e) => setF({ reason: e.target.value })} />
              </div>
              {dialog.mode === "add" && <p className="text-xs text-muted-foreground">This is logged as already approved — it lands on the team calendar and adjusts the member's paycheck immediately.</p>}
              <Button data-testid="admin-save-btn" onClick={save} disabled={busy || (dialog.mode === "add" && !dialog.form.user_id) || !dialog.form.start_date || !dialog.form.end_date || !dialog.form.start_time || !dialog.form.end_time}
                className="w-full bg-[#0A0F1C] hover:bg-indigo-600 text-white transition-colors">
                {busy ? "Saving…" : dialog.mode === "add" ? "Log & approve" : "Save correction"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancelRow} onOpenChange={() => setCancelRow(null)}>
        <DialogContent data-testid="cancel-timeoff-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600">Cancel {cancelRow?.user_name}'s {TYPE_LABEL[cancelRow?.type]}?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {cancelRow?.start_date} → {cancelRow?.end_date} · {cancelRow?.total_hours}h. The member is emailed,
              {cancelRow?.type === "sick" && cancelRow?.status === "approved" ? " their sick hours are refunded," : ""} the calendar event is removed and payroll updates automatically.
            </p>
            <div>
              <Label>Comment (optional)</Label>
              <Textarea data-testid="cancel-comment" className="mt-1.5" rows={2} value={cancelComment} onChange={(e) => setCancelComment(e.target.value)} />
            </div>
            <Button data-testid="confirm-cancel-btn" onClick={doCancel} disabled={busy} className="w-full bg-red-600 hover:bg-red-700 text-white transition-colors">
              {busy ? "Cancelling…" : "Cancel this time off"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
